import React from 'react';
import { Button, InputNumber, Segmented, Typography } from 'antd';

import ChannelBank from './ChannelBank';
import { describeChannelReason } from '../../config/notificationChannels';
import TokenInput from '../common/TokenInput';
import { NotifyAudienceFields } from '../collections/NotifyActionFields';
import { ReadBack } from './TriggerSentence';
import { PLUS, TRASH } from '../icons';
import {
	AUDIENCE_MODES,
	BANK_CHANNELS,
	MAX_STEPS,
	MIN_STEP_GAP_MINUTES,
	describeStep,
	describeUnknownAudience,
} from './notificationEditorModel';

const { Text } = Typography;

/** The step's channels as the bank reads a row: value per key, nothing locked. */
const bankRow = (step, index) => ({
	name: `Rung ${index + 1}`,
	channels: Object.fromEntries(BANK_CHANNELS.map(key => [
		key,
		{ value: (step.channels || []).includes(key), source: 'collection', locked: false },
	])),
});

const audienceSummary = step => {
	const notify = step.notify || {};
	// An audience type this editor does not draw names itself rather than being
	// described as the nearest control we do have.
	if (notify.unknown) return `the ${notify.unknown.type || 'unrecognised'} audience it already names`;
	if (notify.audienceType === 'groups') return `${notify.groupIds?.length || 0} group${notify.groupIds?.length === 1 ? '' : 's'}`;
	if (notify.audienceType === 'users') return `${notify.userIds?.length || 0} named ${notify.userIds?.length === 1 ? 'person' : 'people'}`;
	if (notify.audienceType === 'element') return notify.element ? `whoever the record's ${notify.element} names` : 'a person the record names';
	return `everyone with ${notify.minRole || 'read'} access`;
};

/**
 * Panel 6 — **If nobody answers**: the ladder, as a ladder.
 *
 * A vertical spine with numbered nodes, one card per rung carrying its own
 * controls inline, and an "Add a rung" at the foot. Deliberately NOT a modal
 * per step: a ladder is read as a sequence — *5 minutes, then 20, then the
 * manager* — and a reader who has to open three dialogs to learn that has not
 * been shown a ladder at all, only a list of rows that happen to be ordered.
 *
 * Three rules the shape enforces, because each one is a way a real ladder goes
 * wrong:
 *
 * - **At most five rungs.** A sixth is not urgency, it is noise.
 * - **At least five minutes apart**, so a "ladder" cannot become a burst.
 * - **Delays run from when the alert OPENED**, never from the rung above.
 *   Measuring from the previous step makes the total silently depend on a
 *   delivery that may have been retried; measuring from the opening means
 *   "twenty minutes" means twenty minutes to the person waiting.
 *
 * The channel control is the list's own `ChannelBank` — the same three cells,
 * the same ON/OFF/DEAD vocabulary — so what "text is off" looks like is one
 * fact across the product. Text and push are dead here for the same reason
 * they are dead there, and the reason is given ONCE above the ladder with
 * every dead cell pointing its `aria-describedby` at it.
 */
export default function EscalationLadder({
	steps = [],
	onChange,
	channels = [],
	tokens = [],
	groups = [],
	users = [],
	userElements = null,
	unavailableHintId,
	// A PERSONAL notification's ladder has no audience question in it — see the
	// rung body below.
	personal = false,
}) {
	const patchStep = (index, patch) => onChange(steps.map((step, i) => (i === index ? { ...step, ...patch } : step)));

	const dead = channels.filter(channel => !channel.available);
	// Distinct reasons, so two channels that cannot deliver for the same reason
	// say it once — which is the usual case, and the whole point of putting the
	// sentence beside the CONTROL instead of on every cell.
	const reason = [...new Set(dead.map(channel => channel.reason))].map(describeChannelReason).join(' ');

	const addStep = () => {
		const last = steps[steps.length - 1];
		const after = last ? last.afterMinutes + 15 : MIN_STEP_GAP_MINUTES;
		onChange([...steps, {
			uid: `rung-${Date.now()}-${steps.length}`,
			afterMinutes: after,
			channels: ['email'],
			audienceMode: 'inherit',
			notify: { audienceType: 'role', groupIds: [], userIds: [], minRole: 'read', element: '', unknown: null },
			title: '',
			body: '',
			raw: null,
		}]);
	};

	const toggleChannel = (index, key, next) => {
		const current = steps[index].channels || [];
		patchStep(index, { channels: next ? [...current, key] : current.filter(entry => entry !== key) });
	};

	return (
		<section className="form-panel notification-editor__panel" data-testid="notification-editor-ladder">
			<div className="form-panel__kicker-row">
				<div className="micro-label form-panel__kicker">
					If nobody answers <span className="form-panel__count">{steps.length}</span>
				</div>
			</div>
			<div className="form-panel__hint">
				Each rung fires only while the alert is still open, measured from when it opened —
				not from the rung before it. At most {MAX_STEPS}, at least {MIN_STEP_GAP_MINUTES} minutes apart.
			</div>
			{/* The dead-channel reason, once for the whole ladder rather than on
			    every cell of every rung. Every disabled cell points its
			    `aria-describedby` here, so a screen reader hears it in context
			    instead of five times over. */}
			{dead.length ? (
				<p className="form-panel__hint" id={unavailableHintId}>{reason}</p>
			) : null}

			{steps.length ? (
				<ol className="escalation-ladder">
					{steps.map((step, index) => (
						<li className="escalation-ladder__rung" key={step.uid} data-testid={`notification-editor-rung-${index}`}>
							<span className="escalation-ladder__node" aria-hidden="true">{index + 1}</span>
							<div className="escalation-ladder__card">
								<div className="escalation-ladder__head">
									<span className="escalation-ladder__word">After</span>
									<InputNumber
										className="escalation-ladder__minutes"
										aria-label={`Rung ${index + 1} delay in minutes`}
										min={MIN_STEP_GAP_MINUTES}
										max={60 * 24 * 14}
										value={step.afterMinutes}
										onChange={value => patchStep(index, { afterMinutes: Number.isFinite(value) ? value : MIN_STEP_GAP_MINUTES })}
									/>
									<span className="escalation-ladder__word">minutes, send</span>
									<ChannelBank
										row={bankRow(step, index)}
										channels={channels}
										onToggle={(key, next) => toggleChannel(index, key, next)}
										unavailableHintId={unavailableHintId}
									/>
									<Button
										type="text"
										size="small"
										icon={<TRASH />}
										aria-label={`Remove rung ${index + 1}`}
										onClick={() => onChange(steps.filter((unused, i) => i !== index))}
									/>
								</div>

								<div className="escalation-ladder__audience">
									{/* ⚠ THERE IS NO AUDIENCE QUESTION AT THE PERSONAL TIER.
									    A ladder is an audience with a delay on it, so a rung
									    that could replace its own would hand the whole
									    collection a notification the tier exists to keep to
									    one person — which is why the API refuses any mode but
									    `inherit` at `scope: 'user'`, at authoring, against the
									    stored row, and again when the rung fires. So this is
									    not a picker to disable: it is a question that does not
									    arise, said the way the audience panel above says it. */}
									{personal ? (
										<Text type="secondary" className="d-block">
											You, and nobody else. A rung of a notification you wrote for yourself
											waits longer and tries again — it cannot hand it to anybody new.
										</Text>
									) : (<>
										<Segmented
											aria-label={`Rung ${index + 1} audience`}
											value={step.audienceMode}
											onChange={mode => patchStep(index, { audienceMode: mode })}
											options={AUDIENCE_MODES}
										/>
										{step.audienceMode === 'inherit' ? null : step.notify?.unknown ? (
											// The rung names an audience type this editor does not
											// draw. It reads instead of being picked, for the
											// reason the top-level panel does: a picker here would
											// save back the nearest thing it has.
											<ReadBack
												className="notification-editor__readback"
												text={describeUnknownAudience(step.notify.unknown)}
											/>
										) : (
											// Widening is expressed as an audience and fed back
											// THROUGH the resolver, never unioned onto its output
											// — a wider rung must not hand a revoked reader their
											// access back (Decision 9). That happens on the API;
											// this is the same picker either way.
											<NotifyAudienceFields
												draft={step.notify}
												onChange={notify => patchStep(index, { notify })}
												groups={groups}
												users={users}
												userElements={userElements}
												label={null}
											/>
										)}
									</>)}
								</div>

								<div className="escalation-ladder__words">
									{/* Not `htmlFor`: TokenInput is a contentEditable region,
									    not an input, and carries its own accessible name. */}
									<span className="d-block escalation-ladder__label">Says (optional)</span>
									<TokenInput
										value={step.title}
										tokens={tokens}
										placeholder="Still not logged — please check on Suzy"
										ariaLabel={`Rung ${index + 1} title`}
										onChange={title => patchStep(index, { title })}
									/>
									<Text type="secondary" className="d-block">Leave it empty and the rung repeats the notification's own words.</Text>
								</div>

								<ReadBack
									className="notification-editor__readback escalation-ladder__readback"
									// A personal rung is `inherit` whatever a stored row says
									// — the API reads it back that way at the delivery
									// boundary, and `buildPayload` writes it that way — so the
									// line says the same thing rather than describing a
									// widening that cannot happen.
									text={describeStep(personal ? { ...step, audienceMode: 'inherit' } : step, {
										audienceLabel: personal || step.audienceMode === 'inherit' ? null : audienceSummary(step),
									})}
								/>
							</div>
						</li>
					))}
				</ol>
			) : (
				<Text type="secondary">No ladder — it is sent once and left at that.</Text>
			)}

			<div className="escalation-ladder__add">
				<Button size="small" icon={<PLUS />} onClick={addStep} disabled={steps.length >= MAX_STEPS}>
					Add a rung
				</Button>
				{steps.length >= MAX_STEPS ? (
					<Text type="secondary">{`That is all ${MAX_STEPS}. A sixth is not urgency, it is noise.`}</Text>
				) : null}
			</div>
		</section>
	);
}
